/**
 * Source endpoints — upload, link, text, scoping and retrieval.
 */
import type {
  RegenerateResponse,
  SourceChunksResponse,
  SourceFileResponse,
  SourceItem,
  SourceListResponse,
  SourceUploadResponse,
} from '@/types/api';
import { apiClient } from './client';

/**
 * Source API endpoints
 */
export const sourceApi = {
  /** Upload a file source */
  uploadFile: (lessonId: string, file: File) =>
    apiClient.upload<SourceUploadResponse>(
      `/lessons/${lessonId}/sources/file`,
      file
    ),

  /** Add a link source */
  addLink: (lessonId: string, data: { url: string; name?: string }) =>
    apiClient.post<SourceUploadResponse>(
      `/lessons/${lessonId}/sources/link`,
      data
    ),

  /** Add a text source */
  addText: (
    lessonId: string,
    data: { text: string; name?: string; description?: string }
  ) =>
    apiClient.post<SourceUploadResponse>(
      `/lessons/${lessonId}/sources/text`,
      data
    ),

  /** Apply a scope selection to an AWAITING_SCOPE source: ingest only the
   *  chosen outline sections (by id) or explicit page ranges. */
  scopeSource: (
    lessonId: string,
    sourceId: string,
    selection: {
      section_ids?: string[];
      page_ranges?: { start: number; end: number }[];
      sheet_rows?: Record<string, { start: number; end: number }>;
    }
  ) =>
    apiClient.post<SourceUploadResponse>(
      `/lessons/${lessonId}/sources/${sourceId}/scope`,
      selection
    ),

  /** List sources for a lesson */
  listSources: (lessonId: string) =>
    apiClient.get<SourceListResponse>(`/lessons/${lessonId}/sources`),

  /** Get source details */
  getSource: (sourceId: string) =>
    apiClient.get<SourceItem>(`/sources/${sourceId}`),

  /** Delete (soft remove) a source */
  deleteSource: (sourceId: string) => apiClient.delete(`/sources/${sourceId}`),

  /** Rename a source's display name (does not touch the file or original_filename). */
  renameSource: (sourceId: string, name: string) =>
    apiClient.patch<SourceItem>(`/sources/${sourceId}`, { name }),

  /** Get presigned download URL for a file source */
  getFileUrl: (sourceId: string) =>
    apiClient.get<SourceFileResponse>(`/sources/${sourceId}/file`),

  /** Get pasted-text content for a TEXT-type source. */
  getSourceText: (sourceId: string) =>
    apiClient.get<{ text: string; char_count: number }>(
      `/sources/${sourceId}/text`
    ),

  /** Get extracted chunks (paginated) */
  getChunks: (sourceId: string, params?: { page?: number; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.page) query.append('page', params.page.toString());
    if (params?.limit) query.append('limit', params.limit.toString());
    const queryString = query.toString();
    const endpoint = queryString
      ? `/sources/${sourceId}/chunks?${queryString}`
      : `/sources/${sourceId}/chunks`;
    return apiClient.get<SourceChunksResponse>(endpoint);
  },

  /** Trigger lesson regeneration with updated sources */
  regenerateLesson: (lessonId: string) =>
    apiClient.post<RegenerateResponse>(`/lessons/${lessonId}/regenerate`),
};
